import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Typography,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  useMediaQuery,
  useTheme,
  Alert,
} from "@mui/material";
import { useTypes } from "../../../hooks/useTypes";
import TypeTable from "../../../components/TypeTable";
import AddTypeForm from "../../../components/AddTypeForm";
import EmptyStateRequestTypes from "../../../components/EmptyStateRequestTypes";
import { usePageContext } from "../../../context/PageContext";
import { fetchData } from "../../../api/client";
import { useAuthContext } from "../../../context/AuthContext";
import { useTranslation } from "react-i18next";
import { findSubtypeEditContext } from "../../../utils/typeSubtypeCascade";

const EXPLICACAO_TIPOS =
  "Os tipos de atendimento (e subtipos) definem as categorias das solicitações que sua equipe recebe — por exemplo: Suporte (Problema técnico, Dúvida), Vendas (Orçamento, Consultoria). Isso permite rotear, medir e padronizar o atendimento.";

const Types = () => {
  const { t } = useTranslation("settings");
  const location = useLocation();
  const navigate = useNavigate();
  const reopenedFromCustomFieldsRef = useRef(false);
  const openedFromQueryRef = useRef(false);
  const { currentPage, setCurrentPage } = usePageContext();
  const { userData } = useAuthContext();
  const [pageSize, setPageSize] = useState(50);
  const [search, setSearch] = useState("");
  const [selectedType, setSelectedType] = useState(null);
  const [selectedSubtypeEdit, setSelectedSubtypeEdit] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const {
    data,
    error,
    refetch,
    updateMutation,
    deleteMutation,
    isLoading,
  } = useTypes(currentPage, pageSize, search, true);

  const types = data?.types || [];
  const isEmpty = !isLoading && types.length === 0;
  const [loadingSugestao, setLoadingSugestao] = useState(false);

  /* Reabre edição do tipo ao voltar de Configurações → Campos adicionais */
  useEffect(() => {
    const snapshot = location.state?.returnTypeSnapshot;
    const reopenId = location.state?.reopenTypeId;
    if (!snapshot && !reopenId) return;
    if (reopenedFromCustomFieldsRef.current) return;

    if (snapshot && snapshot.id) {
      reopenedFromCustomFieldsRef.current = true;
      if (snapshot._editKind === "subtype" && snapshot._subtypeLevel) {
        const ctx = findSubtypeEditContext(types, {
          level: snapshot._subtypeLevel,
          sub: snapshot,
          typeId: snapshot._rootTypeId,
          sub1Id: snapshot._sub1Id,
          sub2Id: snapshot._sub2Id,
        });
        if (ctx) {
          setSelectedSubtypeEdit(ctx);
          setSelectedType(null);
        } else {
          setSelectedSubtypeEdit(null);
          setSelectedType(snapshot);
        }
      } else {
        setSelectedSubtypeEdit(null);
        setSelectedType(snapshot);
      }
      navigate(location.pathname, { replace: true, state: null });
      return;
    }

    if (!isLoading && reopenId) {
      const type = types.find((t) => String(t.id) === String(reopenId));
      if (type) {
        reopenedFromCustomFieldsRef.current = true;
        setSelectedType(type);
        navigate(location.pathname, { replace: true, state: null });
      }
    }
  }, [
    location.state,
    location.pathname,
    types,
    isLoading,
    navigate,
  ]);

  useEffect(() => {
    if (!location.state?.reopenTypeId && !location.state?.returnTypeSnapshot) {
      reopenedFromCustomFieldsRef.current = false;
    }
  }, [location.state]);

  useEffect(() => {
    if (!location.search) openedFromQueryRef.current = false;
  }, [location.search]);

  /* Abre o tipo/subtipo indicado na URL (ex.: atalho da tela sem permissão). */
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const typeId = params.get("type");
    if (!typeId || isLoading || !types.length || openedFromQueryRef.current) return;

    const sub1 = params.get("sub1");
    const sub2 = params.get("sub2");
    const sub3 = params.get("sub3");
    let opened = false;

    if (sub3 || sub2 || sub1) {
      const level = sub3 ? 3 : sub2 ? 2 : 1;
      const subId = sub3 || sub2 || sub1;
      const ctx = findSubtypeEditContext(types, {
        level,
        sub: { id: subId },
        typeId,
        sub1Id: sub1,
        sub2Id: sub2,
      });
      if (ctx) {
        setSelectedSubtypeEdit(ctx);
        setSelectedType(null);
        opened = true;
      }
    }

    if (!opened) {
      const type = types.find((t) => String(t.id) === String(typeId));
      if (type) {
        setSelectedSubtypeEdit(null);
        setSelectedType(type);
        opened = true;
      }
    }

    if (opened) {
      openedFromQueryRef.current = true;
      navigate(location.pathname, { replace: true });
    }
  }, [location.search, location.pathname, types, isLoading, navigate]);

  const handleCriarComSugestoesIA = async () => {
    setLoadingSugestao(true);
    setErrorMessage(null);
    try {
      const response = await fetchData("/setup", "POST", {
        etapaAtual: 5,
        dadosEtapa: {},
      });
      const raw = Array.isArray(response?.data)
        ? response.data
        : Array.isArray(response?.data?.data)
        ? response.data.data
        : [];

      if (!raw.length) {
        setErrorMessage("Nenhuma sugestão retornada. Tente novamente.");
        return;
      }

      const byName = new Map();

      const inferTipo = (nome) => {
        const key = (nome || "").toString().trim().toLowerCase();
        if (key.startsWith("atendimento")) return 1; // relacionamento
        if (key.startsWith("venda")) return 0; // comercial
        return 0;
      };

      const ensureRoot = (nome) => {
        const trimmed = (nome || "").trim();
        if (!trimmed) return null;
        const key = trimmed.toLowerCase();
        if (!byName.has(key)) {
          byName.set(key, {
            nome: trimmed,
            tipo: inferTipo(trimmed),
            status: 1,
            ordem: byName.size,
            default: 0,
            children: [],
            id_usuario: userData?.id,
            id_usuario_ultima_alteracao: userData?.id,
          });
        }
        return byName.get(key);
      };

      const ensureChild = (children, nome) => {
        const trimmed = (nome || "").trim();
        if (!trimmed) return null;
        const key = trimmed.toLowerCase();
        let existing = children.find(
          (c) => (c.nome || "").toString().trim().toLowerCase() === key
        );
        if (!existing) {
          existing = {
            nome: trimmed,
            status: 1,
            ordem: children.length,
            default: 0,
            children: [],
          };
          children.push(existing);
        }
        return existing;
      };

      raw.forEach((item) => {
        const root = ensureRoot(item?.nome);
        if (!root) return;

        (item?.subtipos || []).forEach((s1) => {
          const l1 = ensureChild(root.children, s1?.nome);
          if (!l1) return;

          (s1?.subtipos || []).forEach((s2) => {
            const l2 = ensureChild(l1.children || (l1.children = []), s2?.nome);
            if (!l2) return;

            (s2?.subtipos || []).forEach((s3) => {
              ensureChild(l2.children || (l2.children = []), s3?.nome);
            });
          });
        });
      });

      const payloads = Array.from(byName.values());
      if (!payloads.length) {
        setErrorMessage("Nenhuma sugestão válida retornada. Tente novamente.");
        return;
      }

      for (let i = 0; i < payloads.length; i += 1) {
        // eslint-disable-next-line no-await-in-loop
        await updateMutation.mutateAsync(payloads[i]);
      }

      await refetch();
    } catch (err) {
      setErrorMessage(
        err?.response?.data?.message || err?.message || "Erro ao criar tipos com IA."
      );
    } finally {
      setLoadingSugestao(false);
    }
  };

  useEffect(() => {
    if (error) {
      setErrorMessage("Erro ao carregar tipos. Por favor, tente novamente.");
    } else {
      setErrorMessage(null);
    }
  }, [error]);

  /* ========= Helpers ========= */
  const getTypeById = (id) =>
    (data?.types || []).find((t) => String(t.id) === String(id));

  const safeChildren = (node) => (Array.isArray(node?.children) ? [...node.children] : []);

  /* ========= Handlers Nível 0 (Tipo) ========= */
  const handleEditType = (type) => {
    setSelectedSubtypeEdit(null);
    setSelectedType(type);
    setErrorMessage(null);
  };

  const handleEditSubtype = (ctx) => {
    const editCtx = findSubtypeEditContext(types, ctx);
    if (!editCtx) {
      setErrorMessage("Não foi possível carregar o subtipo para edição.");
      return;
    }
    setSelectedType(null);
    setSelectedSubtypeEdit(editCtx);
    setErrorMessage(null);
  };

  const handleDeleteType = (id) => {
    setConfirmDelete(id);
    setErrorMessage(null);
  };

  const confirmDeleteType = () => {
    if (!confirmDelete) return;
    setIsProcessing(true);
    deleteMutation.mutate(confirmDelete, {
      onSuccess: () => {
        setConfirmDelete(null);
        refetch();
      },
      onError: (err) => {
        setErrorMessage(
          err?.response?.data?.message || "Erro ao excluir o tipo."
        );
      },
      onSettled: () => {
        setIsProcessing(false);
      },
    });
  };

  const toggleTypeStatus = (type) => {
    setIsProcessing(true);
    updateMutation.mutate(
      { id: type.id, status: type.status ? 0 : 1 },
      {
        onSuccess: () => refetch(),
        onError: (err) =>
          setErrorMessage(
            err?.response?.data?.message || "Erro ao alterar status do tipo."
          ),
        onSettled: () => setIsProcessing(false),
      }
    );
  };

  /* ========= Handlers Nível 1 (subtipo1) ========= */
  const upsertSubtype1 = ({ typeId, sub }) => {
    const t = getTypeById(typeId);
    if (!t) return;
    const children = safeChildren(t);

    if (sub.id) {
      const idx = children.findIndex((s) => String(s.id) === String(sub.id));
      if (idx >= 0) children[idx] = { ...children[idx], ...sub };
      else children.push({ ...sub });
    } else {
      children.push({ ...sub }); // backend cria id
    }

    setIsProcessing(true);
    updateMutation.mutate(
      { id: typeId, children },
      {
        onSuccess: () => refetch(),
        onError: (err) =>
          setErrorMessage(err?.response?.data?.message || "Erro ao salvar subtipo1."),
        onSettled: () => setIsProcessing(false),
      }
    );
  };

  const deleteSubtype1 = ({ typeId, sub1Id }) => {
    const t = getTypeById(typeId);
    if (!t) return;
    const children = safeChildren(t).filter((s) => String(s.id) !== String(sub1Id));

    setIsProcessing(true);
    updateMutation.mutate(
      { id: typeId, children },
      {
        onSuccess: () => refetch(),
        onError: (err) =>
          setErrorMessage(err?.response?.data?.message || "Erro ao excluir subtipo1."),
        onSettled: () => setIsProcessing(false),
      }
    );
  };

  const toggleSubtype1Status = ({ typeId, sub }) => {
    upsertSubtype1({ typeId, sub: { ...sub, status: sub.status ? 0 : 1 } });
  };

  /* ========= Handlers Nível 2 (subtipo2) ========= */
  const upsertSubtype2 = ({ typeId, sub1Id, sub }) => {
    const t = getTypeById(typeId);
    if (!t) return;
    const level1 = safeChildren(t);
    const i1 = level1.findIndex((s) => String(s.id) === String(sub1Id));
    if (i1 < 0) return;

    const level2 = safeChildren(level1[i1]);
    if (sub.id) {
      const i2 = level2.findIndex((u) => String(u.id) === String(sub.id));
      if (i2 >= 0) level2[i2] = { ...level2[i2], ...sub };
      else level2.push({ ...sub });
    } else {
      level2.push({ ...sub });
    }

    const updatedLevel1 = [...level1];
    updatedLevel1[i1] = { ...updatedLevel1[i1], children: level2 };

    setIsProcessing(true);
    updateMutation.mutate(
      { id: typeId, children: updatedLevel1 },
      {
        onSuccess: () => refetch(),
        onError: (err) =>
          setErrorMessage(err?.response?.data?.message || "Erro ao salvar subtipo2."),
        onSettled: () => setIsProcessing(false),
      }
    );
  };

  const deleteSubtype2 = ({ typeId, sub1Id, sub2Id }) => {
    const t = getTypeById(typeId);
    if (!t) return;
    const level1 = safeChildren(t);
    const i1 = level1.findIndex((s) => String(s.id) === String(sub1Id));
    if (i1 < 0) return;

    const level2 = safeChildren(level1[i1]).filter((u) => String(u.id) !== String(sub2Id));
    const updatedLevel1 = [...level1];
    updatedLevel1[i1] = { ...updatedLevel1[i1], children: level2 };

    setIsProcessing(true);
    updateMutation.mutate(
      { id: typeId, children: updatedLevel1 },
      {
        onSuccess: () => refetch(),
        onError: (err) =>
          setErrorMessage(err?.response?.data?.message || "Erro ao excluir subtipo2."),
        onSettled: () => setIsProcessing(false),
      }
    );
  };

  const toggleSubtype2Status = ({ typeId, sub1Id, sub }) => {
    upsertSubtype2({
      typeId,
      sub1Id,
      sub: { ...sub, status: sub.status ? 0 : 1 },
    });
  };

  /* ========= Handlers Nível 3 (subtipo3) ========= */
  const upsertSubtype3 = ({ typeId, sub1Id, sub2Id, sub }) => {
    const t = getTypeById(typeId);
    if (!t) return;
    const level1 = safeChildren(t);
    const i1 = level1.findIndex((s) => String(s.id) === String(sub1Id));
    if (i1 < 0) return;

    const level2 = safeChildren(level1[i1]);
    const i2 = level2.findIndex((u) => String(u.id) === String(sub2Id));
    if (i2 < 0) return;

    const level3 = safeChildren(level2[i2]);
    if (sub.id) {
      const i3 = level3.findIndex((v) => String(v.id) === String(sub.id));
      if (i3 >= 0) level3[i3] = { ...level3[i3], ...sub };
      else level3.push({ ...sub });
    } else {
      level3.push({ ...sub });
    }

    const updatedLevel2 = [...level2];
    updatedLevel2[i2] = { ...updatedLevel2[i2], children: level3 };

    const updatedLevel1 = [...level1];
    updatedLevel1[i1] = { ...updatedLevel1[i1], children: updatedLevel2 };

    setIsProcessing(true);
    updateMutation.mutate(
      { id: typeId, children: updatedLevel1 },
      {
        onSuccess: () => refetch(),
        onError: (err) =>
          setErrorMessage(err?.response?.data?.message || "Erro ao salvar subtipo3."),
        onSettled: () => setIsProcessing(false),
      }
    );
  };

  const deleteSubtype3 = ({ typeId, sub1Id, sub2Id, sub3Id }) => {
    const t = getTypeById(typeId);
    if (!t) return;
    const level1 = safeChildren(t);
    const i1 = level1.findIndex((s) => String(s.id) === String(sub1Id));
    if (i1 < 0) return;

    const level2 = safeChildren(level1[i1]);
    const i2 = level2.findIndex((u) => String(u.id) === String(sub2Id));
    if (i2 < 0) return;

    const level3 = safeChildren(level2[i2]).filter((v) => String(v.id) !== String(sub3Id));
    const updatedLevel2 = [...level2];
    updatedLevel2[i2] = { ...updatedLevel2[i2], children: level3 };

    const updatedLevel1 = [...level1];
    updatedLevel1[i1] = { ...updatedLevel1[i1], children: updatedLevel2 };

    setIsProcessing(true);
    updateMutation.mutate(
      { id: typeId, children: updatedLevel1 },
      {
        onSuccess: () => refetch(),
        onError: (err) =>
          setErrorMessage(err?.response?.data?.message || "Erro ao excluir subtipo3."),
        onSettled: () => setIsProcessing(false),
      }
    );
  };

  const toggleSubtype3Status = ({ typeId, sub1Id, sub2Id, sub }) => {
    upsertSubtype3({
      typeId,
      sub1Id,
      sub2Id,
      sub: { ...sub, status: sub.status ? 0 : 1 },
    });
  };

  /* ========= Paginação / busca ========= */
  const handlePageChange = (newPage) => setCurrentPage(newPage + 1);
  const handlePageSizeChange = (newPageSize) => setPageSize(newPageSize);
  const handleSearch = (term) => {
    setSearch(term);
    refetch();
  };
  const handleCloseForm = () => {
    setSelectedType(null);
    setSelectedSubtypeEdit(null);
    setErrorMessage(null);
  };

  return (
    <Box sx={{ paddingTop: 1 }}>
      <Box
        sx={{
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          justifyContent: "space-between",
          alignItems: isMobile ? "flex-start" : "center",
          mb: 2,
          gap: isMobile ? 2 : 0,
        }}
      >
        <Typography variant="h4" gutterBottom>
          {t("typesPage.headingTypes")}
        </Typography>
        <Button
          variant="contained"
          color="primary"
          fullWidth={isMobile}
          onClick={() => {
            setSelectedSubtypeEdit(null);
            setSelectedType({});
          }}
        >
          {t("typesPage.addType")}
        </Button>
      </Box>

      {errorMessage && (
        <Box sx={{ mb: 2 }}>
          <Alert severity="error">{errorMessage}</Alert>
        </Box>
      )}

      {selectedType ? (
        <AddTypeForm
          typeData={selectedType}
          entityKind="type"
          isLoading={isLoading || isProcessing}
          onClose={handleCloseForm}
          onSubmit={(updatedType) => {
            setIsProcessing(true);
            updateMutation.mutate(updatedType, {
              onSuccess: () => {
                refetch();
                handleCloseForm();
              },
              onError: (err) =>
                setErrorMessage(
                  err?.response?.data?.message || "Erro ao salvar o tipo."
                ),
              onSettled: () => setIsProcessing(false),
            });
          }}
        />
      ) : selectedSubtypeEdit ? (
        <AddTypeForm
          typeData={selectedSubtypeEdit.typeData}
          entityKind="subtype"
          subtypeLevel={selectedSubtypeEdit.subtypeLevel}
          inheritedConfig={selectedSubtypeEdit.inherited}
          parentLabel={selectedSubtypeEdit.parentLabel}
          rootTypeId={selectedSubtypeEdit.rootType?.id}
          subtypeChainIds={{
            sub1Id: selectedSubtypeEdit.chain?.[1]?.id,
            sub2Id: selectedSubtypeEdit.chain?.[2]?.id,
          }}
          isLoading={isLoading || isProcessing}
          onClose={handleCloseForm}
          onSubmit={(updatedSubtype) => {
            setIsProcessing(true);
            updateMutation.mutate(
              {
                ...updatedSubtype,
                id: selectedSubtypeEdit.typeData.id,
              },
              {
                onSuccess: () => {
                  refetch();
                  handleCloseForm();
                },
                onError: (err) =>
                  setErrorMessage(
                    err?.response?.data?.message || "Erro ao salvar o subtipo."
                  ),
                onSettled: () => setIsProcessing(false),
              }
            );
          }}
        />
      ) : isEmpty ? (
        <EmptyStateRequestTypes
          description={EXPLICACAO_TIPOS}
          onPrimaryClick={handleCriarComSugestoesIA}
          onSecondaryClick={() => setSelectedType({})}
          primaryLoading={loadingSugestao}
        />
      ) : (
        <TypeTable
          /* dados */
          types={types}
          rowCount={data?.pagination?.total || 0}
          pageSize={pageSize}
          page={currentPage - 1}
          isLoading={isLoading || isProcessing}
          /* paginação / busca */
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          onSearch={handleSearch}
          /* ações tipo */
          onEditType={handleEditType}
          onEditSubtype={handleEditSubtype}
          onDeleteType={handleDeleteType}
          onToggleTypeStatus={toggleTypeStatus}
          /* ações subtipo1 */
          onUpsertSubtype1={upsertSubtype1}
          onDeleteSubtype1={deleteSubtype1}
          onToggleSubtype1Status={toggleSubtype1Status}
          /* ações subtipo2 */
          onUpsertSubtype2={upsertSubtype2}
          onDeleteSubtype2={deleteSubtype2}
          onToggleSubtype2Status={toggleSubtype2Status}
          /* ações subtipo3 */
          onUpsertSubtype3={upsertSubtype3}
          onDeleteSubtype3={deleteSubtype3}
          onToggleSubtype3Status={toggleSubtype3Status}
        />
      )}

      <Dialog open={!!confirmDelete} onClose={() => setConfirmDelete(null)}>
        <DialogTitle>Confirmação de Exclusão</DialogTitle>
        <DialogContent>
          <Typography>
            {isProcessing
              ? "Processando exclusão..."
              : "Tem certeza de que deseja excluir este tipo?"}
          </Typography>
        </DialogContent>
        <DialogActions>
          {!isProcessing && (
            <>
              <Button onClick={() => setConfirmDelete(null)}>Cancelar</Button>
              <Button color="error" onClick={confirmDeleteType}>
                Excluir
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Types;
